import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { AVATARS, SETTING_LIMITS } from '@shared/rules';
import type { PackSummary, RoomSettings } from '@shared/types';
import { Avatar, copyText, roomLink, whatsappLink } from '../../components/Chrome';
import { usePrefs } from '../../components/Prefs';
import { Sheet } from '../../components/Sheet';
import { api } from '../../lib/api';
import { getMyPacks, saveAvatar } from '../../lib/storage';
import { accountApi } from '../account/api';
import { useAccount } from '../account/session';
import type { RoomApi } from '../../lib/useRoom';
import { ModeSettings, MoveToTeam, TeamsPanel } from './LobbyModes';

export function Lobby({ room }: { room: RoomApi }) {
  const view = room.view!;
  const { toast } = usePrefs();
  const isHost = view.me?.id === view.hostId;
  const online = view.players.filter((p) => p.connected).length;
  const needed = Math.max(0, view.minPlayers - online);
  const [avatarSheet, setAvatarSheet] = useState(false);
  const [starting, setStarting] = useState(false);

  async function start() {
    setStarting(true);
    const err = await room.act('game:start', {});
    setStarting(false);
    if (err) toast(err, 'error');
  }

  const invite = `Come play CompleteMasti with us! Room code ${view.code}: ${roomLink(view.code)}`;

  return (
    <>
      <main className="game-main" style={{ maxWidth: 760 }}>
        <section className="panel center stack" style={{ gap: 8 }}>
          <span className="muted small" style={{ fontWeight: 800 }}>
            ROOM CODE
          </span>
          <div className="big-code">{view.code}</div>
          <p className="muted small" style={{ margin: 0 }}>
            Friends join at <strong>{window.location.host}</strong> with this code.
          </p>
          <div className="row" style={{ justifyContent: 'center', marginTop: 6 }}>
            <a className="btn btn-sm btn-primary" href={whatsappLink(invite)} target="_blank" rel="noreferrer">
              Invite on WhatsApp
            </a>
            <button
              type="button"
              className="btn btn-sm btn-outline"
              onClick={async () => {
                if (await copyText(roomLink(view.code))) toast('Invite link copied.');
              }}
            >
              Copy link
            </button>
            <a className="btn btn-sm btn-outline" href={`/tv/${view.code}`} target="_blank" rel="noreferrer">
              TV mode
            </a>
          </div>
        </section>

        <section className="panel">
          <div className="row between">
            <h2 style={{ margin: 0 }}>
              Players <span className="muted">({view.players.length})</span>
            </h2>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAvatarSheet(true)}>
              Change my emoji
            </button>
          </div>
          <ul className="player-list" style={{ marginTop: 10 }}>
            {view.players.map((p) => (
              <li key={p.id}>
                <Avatar player={p} />
                <span className="name">
                  {p.name}
                  {p.id === view.me?.id && <span className="muted"> (you)</span>}
                </span>
                {!p.connected && <span className="chip">away</span>}
                {p.isHost && <span className="chip blue">host</span>}
                {isHost && p.id !== view.me?.id && <PlayerActions room={room} playerId={p.id} name={p.name} />}
              </li>
            ))}
          </ul>
          {needed > 0 && (
            <p className="notice" style={{ marginTop: 12, marginBottom: 0 }}>
              Waiting for {needed} more {needed === 1 ? 'player' : 'players'}. You need at least {view.minPlayers} to play.
            </p>
          )}
        </section>

        <TeamsPanel room={room} />

        <SettingsPanel room={room} />
      </main>

      <div className="action-bar">
        <div className="inner">
          {isHost ? (
            <button className="btn btn-primary btn-lg btn-block" disabled={needed > 0 || starting} onClick={start}>
              {starting ? 'Shuffling...' : needed > 0 ? `Need ${needed} more to start` : `Start game (${online} players)`}
            </button>
          ) : (
            <p className="status-line center grow" style={{ margin: 0 }}>
              Waiting for {view.players.find((p) => p.isHost)?.name ?? 'the host'} to start...
            </p>
          )}
        </div>
      </div>

      {avatarSheet && (
        <Sheet title="Pick your emoji" onClose={() => setAvatarSheet(false)}>
          <div className="avatar-grid">
            {AVATARS.map((a) => {
              const taken = view.players.some((p) => p.avatar === a && p.id !== view.me?.id);
              return (
                <button
                  key={a}
                  type="button"
                  disabled={taken}
                  aria-pressed={view.players.find((p) => p.id === view.me?.id)?.avatar === a}
                  onClick={async () => {
                    const err = await room.act('player:avatar', { avatar: a });
                    if (err) toast(err, 'error');
                    else {
                      saveAvatar(a);
                      setAvatarSheet(false);
                    }
                  }}
                >
                  {a}
                </button>
              );
            })}
          </div>
        </Sheet>
      )}
    </>
  );
}

function PlayerActions({ room, playerId, name }: { room: RoomApi; playerId: string; name: string }) {
  const { toast } = usePrefs();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="icon-btn" aria-label={`Options for ${name}`} onClick={() => setOpen(true)}>
        ⋯
      </button>
      {open && (
        <Sheet title={name} onClose={() => setOpen(false)}>
          <div className="menu-list">
            <button
              type="button"
              onClick={async () => {
                const err = await room.act('host:transfer', { playerId });
                if (err) toast(err, 'error');
                setOpen(false);
              }}
            >
              ⭐ Make {name} the host
            </button>
            <MoveToTeam room={room} playerId={playerId} onDone={() => setOpen(false)} />
            <button
              type="button"
              className="danger"
              onClick={async () => {
                if (!window.confirm(`Remove ${name} from the room?`)) return;
                const err = await room.act('player:kick', { playerId });
                if (err) toast(err, 'error');
                setOpen(false);
              }}
            >
              🚫 Remove {name}
            </button>
          </div>
        </Sheet>
      )}
    </>
  );
}

const POINT_LABELS: Record<number, string> = { 0: 'No limit' };
const TIMER_LABELS: Record<number, string> = { 0: 'Off', 60: '1 min', 90: '1.5 min', 120: '2 min' };

export function SettingsPanel({ room }: { room: RoomApi }) {
  const view = room.view!;
  const { toast } = usePrefs();
  const isHost = view.me?.id === view.hostId;
  const s = view.settings;

  async function update(patch: Partial<RoomSettings>) {
    const err = await room.act('settings:update', patch);
    if (err) toast(err, 'error');
  }

  return (
    <>
      <section className="panel stack">
        <div className="row between">
          <h2 style={{ margin: 0 }}>Game settings</h2>
          {!isHost && <span className="chip">host picks</span>}
        </div>

        <Setting label="Who picks the winner?">
          <Choice
            disabled={!isHost}
            value={s.judgeMode}
            options={[
              ['judge', '👑 Rotating judge'],
              ['vote', '🗳️ Everyone votes'],
            ]}
            onChange={(v) => update({ judgeMode: v as RoomSettings['judgeMode'] })}
          />
          <span className="hint muted small">
            {s.judgeMode === 'judge'
              ? 'Classic: one person judges each round and reads the cards out.'
              : 'Panchayat style: everyone plays every round and votes for their favourite (not their own).'}
          </span>
        </Setting>

        <Setting label="Points to win">
          <Choice
            disabled={!isHost}
            value={s.pointsToWin}
            options={SETTING_LIMITS.pointsToWin.map((n) => [n, POINT_LABELS[n] ?? String(n)])}
            onChange={(v) => update({ pointsToWin: Number(v) })}
          />
        </Setting>

        <Setting label="Round timer">
          <Choice
            disabled={!isHost}
            value={s.roundTimer}
            options={SETTING_LIMITS.roundTimer.map((n) => [n, TIMER_LABELS[n]])}
            onChange={(v) => update({ roundTimer: Number(v) })}
          />
        </Setting>

        <div className="grid-2">
          <Stepper
            label="Cards in hand"
            value={s.handSize}
            min={SETTING_LIMITS.handSize.min}
            max={SETTING_LIMITS.handSize.max}
            disabled={!isHost}
            onChange={(v) => update({ handSize: v })}
          />
          <Stepper
            label="Blank cards each"
            hint="Write your own answer"
            value={s.writeIns}
            min={SETTING_LIMITS.writeIns.min}
            max={SETTING_LIMITS.writeIns.max}
            disabled={!isHost}
            onChange={(v) => update({ writeIns: v })}
          />
          <Stepper
            label="Hand swaps each"
            hint="Trade in a bad hand"
            value={s.redraws}
            min={SETTING_LIMITS.redraws.min}
            max={SETTING_LIMITS.redraws.max}
            disabled={!isHost}
            onChange={(v) => update({ redraws: v })}
          />
        </div>
      </section>

      <ModeSettings room={room} />

      <PacksPanel room={room} />
    </>
  );
}

function Setting({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="stack" style={{ gap: 6 }}>
      <strong className="small">{label}</strong>
      {children}
    </div>
  );
}

function Choice<T extends string | number>({
  value,
  options,
  onChange,
  disabled,
}: {
  value: T;
  options: [T, string][];
  onChange: (v: T) => void;
  disabled?: boolean;
}) {
  return (
    <div className="segmented" role="group">
      {options.map(([v, label]) => (
        <button key={String(v)} type="button" aria-pressed={v === value} disabled={disabled && v !== value} onClick={() => !disabled && onChange(v)}>
          {label}
        </button>
      ))}
    </div>
  );
}

function Stepper({
  label,
  hint,
  value,
  min,
  max,
  disabled,
  onChange,
}: {
  label: string;
  hint?: string;
  value: number;
  min: number;
  max: number;
  disabled?: boolean;
  onChange: (v: number) => void;
}) {
  return (
    <div className="row between" style={{ flexWrap: 'nowrap' }}>
      <div>
        <strong className="small">{label}</strong>
        {hint && <div className="muted small">{hint}</div>}
      </div>
      <div className="row" style={{ flexWrap: 'nowrap', gap: 4 }}>
        {!disabled && (
          <button type="button" className="icon-btn" aria-label={`Fewer: ${label}`} disabled={value <= min} onClick={() => onChange(value - 1)}>
            −
          </button>
        )}
        <strong style={{ minWidth: 24, textAlign: 'center', fontSize: '1.1rem' }}>{value}</strong>
        {!disabled && (
          <button type="button" className="icon-btn" aria-label={`More: ${label}`} disabled={value >= max} onClick={() => onChange(value + 1)}>
            +
          </button>
        )}
      </div>
    </div>
  );
}

function PacksPanel({ room }: { room: RoomApi }) {
  const view = room.view!;
  const { toast } = usePrefs();
  const isHost = view.me?.id === view.hostId;
  const [available, setAvailable] = useState<PackSummary[]>([]);
  const [code, setCode] = useState('');
  const [adding, setAdding] = useState(false);
  const selected = new Set(view.settings.packIds);

  const [accountPacks, setAccountPacks] = useState<{ code: string; name: string }[]>([]);
  const account = useAccount();
  const signedIn = Boolean(account.user);

  useEffect(() => {
    api.packs().then(setAvailable).catch(() => {});
  }, []);

  // Signed-in hosts also get their account's packs as quick adds.
  useEffect(() => {
    if (!signedIn) return;
    accountApi
      .packs()
      .then((packs) => setAccountPacks(packs.map((p) => ({ code: p.code, name: p.name }))))
      .catch(() => {});
  }, [signedIn]);

  // Public packs, plus any community pack already in the room.
  const all = [...available];
  for (const p of view.packs) if (!all.some((a) => a.id === p.id)) all.push(p);
  const shown = isHost ? all : all.filter((p) => selected.has(p.id));
  const quickPacks = [...accountPacks, ...getMyPacks()].filter((m, i, list) => list.findIndex((x) => x.code === m.code) === i);
  const myPacks = quickPacks.filter((m) => !all.some((p) => p.code === m.code && selected.has(p.id)));
  const totals = view.packs.reduce((t, p) => ({ prompts: t.prompts + p.promptCount, answers: t.answers + p.answerCount }), { prompts: 0, answers: 0 });

  async function toggle(pack: PackSummary) {
    const on = selected.has(pack.id);
    if (!on && pack.nsfw && !window.confirm(`${pack.name} is for adults only. Is everyone in this room 18+ and up for it?`)) return;
    const packIds = on ? view.settings.packIds.filter((id) => id !== pack.id) : [...view.settings.packIds, pack.id];
    const err = await room.act('settings:update', { packIds });
    if (err) toast(err, 'error');
  }

  async function addByCode(value: string) {
    if (!value.trim()) return;
    setAdding(true);
    const res = await room.act('packs:add-code', { code: value.trim() });
    setAdding(false);
    if (res) toast(res, 'error');
    else {
      toast('Pack added.', 'success');
      setCode('');
    }
  }

  return (
    <section className="panel stack">
      <div className="row between">
        <h2 style={{ margin: 0 }}>Card packs</h2>
        <span className="muted small">
          {totals.prompts} prompts · {totals.answers} answers
        </span>
      </div>
      <div className="pack-list">
        {shown.map((p) => (
          <button key={p.id} type="button" className="pack-option" aria-pressed={selected.has(p.id)} disabled={!isHost} onClick={() => toggle(p)}>
            <span className="tick">{selected.has(p.id) ? '✓' : ''}</span>
            <span className="grow">
              <span className="row" style={{ gap: 6 }}>
                <strong>{p.name}</strong>
                {p.nsfw && <span className="chip red">18+</span>}
                {p.kind === 'community' && <span className="chip orange">custom · {p.code}</span>}
              </span>
              <span className="muted small" style={{ display: 'block' }}>
                {p.description || `${p.promptCount} prompts, ${p.answerCount} answers`}
              </span>
            </span>
          </button>
        ))}
        {!shown.length && <p className="muted">No packs picked yet.</p>}
      </div>
      {isHost && (
        <>
          <form
            className="row"
            style={{ flexWrap: 'nowrap' }}
            onSubmit={(e) => {
              e.preventDefault();
              void addByCode(code);
            }}
          >
            <input className="input" placeholder="Add a custom pack by code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={12} />
            <button className="btn btn-blue" disabled={adding || !code.trim()}>
              Add
            </button>
          </form>
          {myPacks.length > 0 && (
            <div className="row">
              <span className="muted small">Your packs:</span>
              {myPacks.map((m) => (
                <button key={m.code} type="button" className="btn btn-sm btn-outline" onClick={() => addByCode(m.code)}>
                  + {m.name}
                </button>
              ))}
            </div>
          )}
          <p className="muted small" style={{ margin: 0 }}>
            Want inside jokes in the deck? <Link to="/packs/new" target="_blank">Make your own pack</Link> in a couple of minutes.
          </p>
        </>
      )}
    </section>
  );
}
